import { useState } from 'react';
import { Box } from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import UserSidebar from '../components/layout/UserSidebar';
import UserHeader from '../components/layout/UserHeader';

const drawerWidth = 280;
const appBarHeight = 78;

interface MainLayoutProps {
  children: React.ReactNode;
}

export default function MainLayout({ children }: MainLayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<HTMLElement | null>(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopDrawerToggle = () => {
    setDesktopOpen(!desktopOpen);
  };

  const handleLogout = () => {
    logout();
  };

  const handleProfileSettings = () => {
    navigate('/profile');
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <UserSidebar
        user={user}
        mobileOpen={mobileOpen}
        desktopOpen={desktopOpen}
        onDrawerToggle={handleDrawerToggle}
        onDesktopDrawerToggle={handleDesktopDrawerToggle}
        onProfileSettings={handleProfileSettings}
        onLogout={handleLogout}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: {
            xs: '100%',
            sm: desktopOpen ? `calc(100% - ${drawerWidth}px)` : 'calc(100% - 64px)',
          },
          transition: 'width 0.3s ease-in-out',
          minHeight: '100vh',
          backgroundColor: 'grey.50',
        }}
      >
        {/* Gradient header bar: mobile only; desktop has sidebar, no top bar */}
        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
          <UserHeader
            user={user}
            anchorEl={profileMenuAnchor}
            onProfileMenuOpen={(e: React.MouseEvent<HTMLElement>) => setProfileMenuAnchor(e.currentTarget)}
            onProfileMenuClose={() => setProfileMenuAnchor(null)}
            onLogout={handleLogout}
            onProfileSettings={handleProfileSettings}
            onDrawerToggle={handleDrawerToggle}
            onDesktopDrawerToggle={handleDesktopDrawerToggle}
            desktopOpen={desktopOpen}
            location={location}
          />
        </Box>
        <Box
          sx={{
            pt: { xs: `${appBarHeight}px`, sm: 0 },
            p: 3,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}